import type { ColumnsType } from 'antd/es/table'

import { useQuery } from '@tanstack/react-query'
import { Table, Tag } from 'antd'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useMemo, useReducer, useState } from 'react'
import { useDebounce } from 'react-use'

import $api from '../../API'
import { EmptyCellValue } from '../../components/CellsGrid/EmptyCellValue/EmptyCellValue'

import { Filters, SummaryCard } from './components'
import { baseCellClassName, baseHeaderCellClassName, currencyFormatter } from './constants'
import { initialState, reducer } from './store'
import type { CACheckRegisterResponse, CACheckRegisterRow } from './types'

export function CAChecksPage(): React.ReactNode {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [groupSearchValue, setGroupSearchValue] = useState('')
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('')

  useDebounce(
    () => {
      setDebouncedGroupSearch(groupSearchValue)
    },
    300,
    [groupSearchValue]
  )

  const groupsQuery = useQuery({
    queryKey: ['ca-groups', { search: debouncedGroupSearch }] as const,
    async queryFn({ queryKey, signal }) {
      const [, queryParams] = queryKey
      const params = new URLSearchParams()
      if (queryParams.search) {
        params.set('search', queryParams.search)
      }

      const response = await $api.get<{ id: string; number: string; name: string }[]>(
        `/ca/groups?${params.toString()}`,
        { signal }
      )
      return response.data
    },
    keepPreviousData: false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  })

  const selectedGroup = useMemo(() => {
    if (!state.filters.groupNumber || !groupsQuery.data) return null
    return groupsQuery.data.find(g => g.number === state.filters.groupNumber) ?? null
  }, [state.filters.groupNumber, groupsQuery.data])

  const checkRegisterQuery = useQuery({
    queryKey: [
      'ca-checks',
      {
        groupNumber: state.filters.groupNumber,
        view: state.filters.view,
        dateFrom: state.filters.dateFrom,
        dateTo: state.filters.dateTo,
        checkNumber: state.filters.checkNumber,
        page: state.pagination.page,
        pageSize: state.pagination.pageSize
      }
    ] as const,
    async queryFn({ queryKey, signal }) {
      const [, params] = queryKey

      const searchParams = new URLSearchParams()
      if (params.groupNumber) {
        searchParams.set('group-number', params.groupNumber)
      }
      searchParams.set('view', params.view)
      if (params.dateFrom) {
        searchParams.set('date-from', params.dateFrom)
      }
      if (params.dateTo) {
        searchParams.set('date-to', params.dateTo)
      }
      if (params.checkNumber) {
        searchParams.set('check-number', params.checkNumber)
      }
      searchParams.set('page', params.page.toString())
      searchParams.set('page-size', params.pageSize.toString())

      const response = await $api.get<CACheckRegisterResponse>(
        `/ca/checks?${searchParams.toString()}`,
        { signal }
      )
      return response.data
    }
  })

  const handleRefresh = useCallback(() => {
    void checkRegisterQuery.refetch()
  }, [checkRegisterQuery])

  const columns = useMemo<ColumnsType<CACheckRegisterRow>>(
    () => [
      {
        title: 'Check #',
        dataIndex: 'checkNumber',
        width: 120,
        onCell: () => ({ className: cx(baseCellClassName, 'font-mono') }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Check Date',
        dataIndex: 'checkDate',
        width: 110,
        render: (value: string | null) =>
          value ? dayjs(value).format('MM/DD/YYYY') : <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Claim #',
        dataIndex: 'claimNumber',
        width: 120,
        render: (value: number | null) =>
          value ? <span className='font-mono'>{value}</span> : <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Payee',
        dataIndex: 'payee',
        ellipsis: true,
        render: (value: string | null) => value ?? <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Amount',
        dataIndex: 'checkAmount',
        width: 120,
        align: 'right',
        render: (value: number) => (
          <span className={cx('font-mono', value < 0 && 'text-red-600')}>
            {currencyFormatter.format(value)}
          </span>
        ),
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 100,
        align: 'center',
        render: (value: CACheckRegisterRow['status']) => {
          const config: Record<typeof value, { color: string; label: string }> = {
            issued: { color: 'green', label: 'Issued' },
            void: { color: 'red', label: 'Void' },
            adjustment: { color: 'orange', label: 'Adjustment' }
          }
          const { color, label } = config[value] ?? { color: 'default', label: value }
          return <Tag color={color}>{label}</Tag>
        },
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      }
    ],
    []
  )

  const summary = checkRegisterQuery.data?.summary

  return (
    <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
      <div className='flex justify-between items-start'>
        <h1 className='text-2xl'>CA Checks</h1>
      </div>

      <div className='flex flex-col rounded-lg border bg-white'>
        <Filters
          filters={state.filters}
          groups={groupsQuery.data}
          groupsLoading={groupsQuery.isFetching}
          isRefreshing={checkRegisterQuery.isFetching}
          showDateFilters
          groupSearchValue={groupSearchValue}
          onGroupSearch={setGroupSearchValue}
          onGroupChange={value => dispatch({ type: 'filters/groupNumber/set', payload: value })}
          onDateFromChange={value => dispatch({ type: 'filters/dateFrom/set', payload: value })}
          onDateToChange={value => dispatch({ type: 'filters/dateTo/set', payload: value })}
          onCheckNumberChange={value =>
            dispatch({ type: 'filters/checkNumber/set', payload: value })
          }
          onRefresh={handleRefresh}
        />

        {state.filters.view === 'current' && state.filters.groupNumber && (
          <div className='p-6 border-b border-dashed text-center font-mono text-sm'>
            <div className='tracking-[0.5em] font-bold mb-2'>MONTHLY CHECK REGISTER</div>
            <div>LUCENT HEALTH SOLUTIONS</div>
            <div>Date: {dayjs().format('MM/DD/YYYY')}</div>
            <div>Group: {selectedGroup?.name ?? ''}</div>
            {state.filters.dateFrom && state.filters.dateTo && (
              <div>
                From: {dayjs(state.filters.dateFrom).format('MM/DD/YYYY')} Thru:{' '}
                {dayjs(state.filters.dateTo).format('MM/DD/YYYY')}
              </div>
            )}
          </div>
        )}

        {state.filters.view === 'proposed' && summary && (
          <div className='flex gap-4 p-4 border-b bg-gray-50'>
            <SummaryCard label='Total Checks' value={summary.totalChecks.toLocaleString()} />
            <SummaryCard
              label='Net Amount'
              value={currencyFormatter.format(summary.totalAmount)}
              highlight
            />
            <SummaryCard
              label='Issued'
              value={summary.issuedCount.toLocaleString()}
              color='green'
            />
            <SummaryCard
              label='Adjustments'
              value={summary.adjustmentCount.toLocaleString()}
              color='orange'
            />
            <SummaryCard label='Voids' value={summary.voidCount.toLocaleString()} color='red' />
          </div>
        )}

        <Table
          size='small'
          rowKey='id'
          columns={columns}
          dataSource={checkRegisterQuery.data?.data ?? []}
          loading={checkRegisterQuery.isFetching}
          className='rounded-none payment-files-table'
          pagination={{
            current: state.pagination.page,
            pageSize: state.pagination.pageSize,
            total: checkRegisterQuery.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            showTotal: (total, [from, to]) => (
              <span className='text-sm text-gray-500'>
                Showing {from}–{to} of {total} records
              </span>
            )
          }}
          onChange={pagination => {
            if (pagination.current !== state.pagination.page) {
              dispatch({ type: 'pagination/page/set', payload: pagination.current ?? 1 })
            }
            if (pagination.pageSize !== state.pagination.pageSize) {
              dispatch({ type: 'pagination/pageSize/set', payload: pagination.pageSize ?? 50 })
            }
          }}
        />
      </div>
    </div>
  )
}
